import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer,
  ReferenceArea
} from 'recharts';
import { Empty, Spin, Radio, RadioChangeEvent, Space, Divider, Button, message } from 'antd';
import { BaseText } from 'src/components/typography';
import { getTrackDass } from 'src/services/client-service';
import { EDASS_HOMEWORK } from 'src/variables/enum-variables';
import { TDass, THomeworkSummaryScore } from 'src/interfaces/clients-interface';
import { showErrorToast } from 'src/components/toast/Toast';
import ResponseError from 'src/interfaces/error-response-interface';
import ModalSeverity from './ModalSeverity';
import ChartLegend from './ChartLegend';
import './Dass.scss';
import { calculateDassStatistics } from './utils/statistics';
import { getDass21SeverityRanges, getDass42SeverityRanges, getSeverityForScore, SEVERITY_COLORS, DassSeverityCategory } from './utils/severity';
import DassStatistics from './DassStatistics';
import jsPDF from 'jspdf';
import dayjs from 'dayjs';
import { FilePdfOutlined } from '@ant-design/icons';
import logoImage from 'src/assets/images/logo.png';

interface DassSeverityData {
  depression: number[];
  anxiety: number[];
  stress: number[];
}

interface DassEntry {
  date: string;
  depression: number;
  anxiety: number;
  stress: number;
  depressionPercentile?: number | null;
  anxietyPercentile?: number | null;
  stressPercentile?: number | null;
}

interface VisibleLines {
  depression: boolean;
  anxiety: boolean;
  stress: boolean;
}

const DASS_COLORS = {
  depression: '#2E5BFF',
  anxiety: '#8C54FF',
  stress: '#00C1D4'
};

// Define percentile severity ranges
const PERCENTILE_SEVERITY_RANGES = [
  { category: 'Normal', from: 0, to: 50, color: SEVERITY_COLORS.Normal },
  { category: 'Mild', from: 50, to: 70, color: SEVERITY_COLORS.Mild },
  { category: 'Moderate', from: 70, to: 85, color: SEVERITY_COLORS.Moderate },
  { category: 'Severe', from: 85, to: 95, color: SEVERITY_COLORS.Severe },
  { category: 'Extremely Severe', from: 95, to: 100, color: SEVERITY_COLORS['Extremely Severe'] }
];

// Function to get severity category based on percentile
const getSeverityForPercentile = (percentile: number): { category: DassSeverityCategory, color: string } => {
  const range = PERCENTILE_SEVERITY_RANGES.find(r => percentile >= r.from && percentile <= r.to);
  return {
    category: (range?.category || 'Normal') as DassSeverityCategory,
    color: range?.color || SEVERITY_COLORS.Normal
  };
};

const Dass = () => {
  const params = useParams();
  const clientId = params?.clientId || '';
  const [selectedDass, setSelectedDass] = useState<TDass | null>(null);
  const [isShowSeverity, setIsShowSeverity] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'scores' | 'percentiles'>('scores');
  const [displayMode, setDisplayMode] = useState<'combined' | 'separate'>('separate');
  const [dassData, setDassData] = useState<{
    dass21: TDass | null;
    dass42: TDass | null;
  }>({
    dass21: null,
    dass42: null
  });
  const [visibleLines, setVisibleLines] = useState<VisibleLines>({
    depression: true,
    anxiety: true,
    stress: true
  });

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      setLoading(true);
      const [dass21Response, dass42Response] = await Promise.all([
        getTrackDass(clientId, EDASS_HOMEWORK.DASS21),
        getTrackDass(clientId, EDASS_HOMEWORK.DASS42)
      ]);

      console.log('DASS-21 Raw Response:', dass21Response);
      console.log('DASS-42 Raw Response:', dass42Response);

      setDassData({
        dass21: dass21Response,
        dass42: dass42Response
      });
    } catch (e) {
      showErrorToast((e as ResponseError).message);
    } finally {
      setLoading(false);
    }
  };

  const formatChartData = (data: Array<{ 
    createdAt: string; 
    summary: THomeworkSummaryScore;
    percentiles?: {
      Depression: number | null;
      Anxiety: number | null;
      Stress: number | null;
    };
  }>) => {
    console.log('Formatting data:', data);
    const formattedData = data.map(entry => {
      const formatted = {
        date: new Date(entry.createdAt).toLocaleDateString(),
        depression: entry.summary?.Depression?.score || 0,
        anxiety: entry.summary?.Anxiety?.score || 0,
        stress: entry.summary?.Stress?.score || 0,
        depressionPercentile: entry.percentiles?.Depression || 0,
        anxietyPercentile: entry.percentiles?.Anxiety || 0,
        stressPercentile: entry.percentiles?.Stress || 0
      };
      console.log('Formatted entry:', formatted);
      return formatted;
    });
    console.log('Final formatted data:', formattedData);
    return formattedData;
  };

  const handleLegendClick = (dataKey: keyof VisibleLines) => {
    setVisibleLines(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  const renderSeverityRanges = (type: 'DASS-21' | 'DASS-42', metric: 'Depression' | 'Anxiety' | 'Stress') => {
    const ranges = type === 'DASS-21' ? getDass21SeverityRanges(metric) : getDass42SeverityRanges(metric);
    return ranges.map((range, index) => (
      <ReferenceArea
        key={`${metric}-${range.category}`}
        y1={range.from}
        y2={range.to}
        fill={range.color}
        fillOpacity={0.1}
        strokeOpacity={0}
      />
    ));
  };

  const renderPercentileSeverityRanges = () => {
    return PERCENTILE_SEVERITY_RANGES.map((range) => (
      <ReferenceArea
        key={`percentile-${range.category}`}
        y1={range.from}
        y2={range.to}
        fill={range.color}
        fillOpacity={0.1}
        strokeOpacity={0}
      />
    ));
  };

  const renderSeverityLegend = () => {
    return (
      <div className="severity-legend">
        <BaseText type="caption" className="legend-title">Severity Ranges:</BaseText>
        <div className="severity-legend-items">
          {(Object.keys(SEVERITY_COLORS) as DassSeverityCategory[]).map((category) => (
            <div key={category} className="severity-legend-item">
              <span 
                className="color-box" 
                style={{ backgroundColor: SEVERITY_COLORS[category] }} 
              />
              <BaseText type="caption">{category}</BaseText>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSingleMetricChart = (
    chartData: DassEntry[], 
    metric: 'depression' | 'anxiety' | 'stress',
    title: string,
    version: 'DASS-21' | 'DASS-42',
    yDomain: [number, number]
  ) => {
    const metricKey = viewMode === 'scores' 
      ? metric 
      : `${metric}Percentile` as 'depressionPercentile' | 'anxietyPercentile' | 'stressPercentile';
    
    const metricTitle = metric.charAt(0).toUpperCase() + metric.slice(1);
    const color = DASS_COLORS[metric as keyof typeof DASS_COLORS];
    const curveType: 'monotone' | undefined = chartData.length === 1 ? undefined : 'monotone';

    // Set custom Y-axis domain based on metric and view mode
    let customYDomain: [number, number] = yDomain;
    
    if (viewMode === 'scores') {
      // Use appropriate max values based on DASS severity ranges for each metric
      if (version === 'DASS-21') {
        switch (metric) {
          case 'depression':
            customYDomain = [0, 17]; // Max is 14+ for extremely severe
            break;
          case 'anxiety':
            customYDomain = [0, 13]; // Max is 10+ for extremely severe
            break;
          case 'stress':
            customYDomain = [0, 20]; // Max is 17+ for extremely severe
            break;
        }
      } else {
        // DASS-42 has higher ranges
        switch (metric) {
          case 'depression':
            customYDomain = [0, 34]; // Max is 28+ for extremely severe
            break;
          case 'anxiety':
            customYDomain = [0, 26]; // Max is 20+ for extremely severe
            break;
          case 'stress':
            customYDomain = [0, 40]; // Max is 34+ for extremely severe
            break;
        }
      }
    } else {
      // For percentiles, always use 0-100
      customYDomain = [0, 100];
    }
    
    // Get the most recent value for this metric
    const latestEntry = chartData.length > 0 
      ? chartData[chartData.length - 1] 
      : null;
    
    const currentValue = latestEntry 
      ? viewMode === 'scores' 
        ? latestEntry[metric] 
        : latestEntry[`${metric}Percentile` as keyof typeof latestEntry]
      : 0;
    
    // Get severity information for the current score
    const severityInfo = viewMode === 'scores' && latestEntry
      ? getSeverityForScore(
          latestEntry[metric], 
          metricTitle as 'Depression' | 'Anxiety' | 'Stress',
          version
        )
      : viewMode === 'percentiles' && latestEntry && latestEntry[`${metric}Percentile` as keyof typeof latestEntry]
        ? getSeverityForPercentile(Number(latestEntry[`${metric}Percentile` as keyof typeof latestEntry]))
        : null;

    // Get severity ranges for creating background areas
    const severityRanges = viewMode === 'scores'
      ? version === 'DASS-21'
        ? getDass21SeverityRanges(metricTitle as 'Depression' | 'Anxiety' | 'Stress')
        : getDass42SeverityRanges(metricTitle as 'Depression' | 'Anxiety' | 'Stress')
      : PERCENTILE_SEVERITY_RANGES;

    return (
      <div className="single-metric-chart">
        <BaseText type="subHeading" className="metric-title">{metricTitle}</BaseText>
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.5} />
              
              {/* Add background colors for severity ranges */}
              {viewMode === 'scores' && severityRanges.map((range) => (
                <ReferenceArea
                  key={`${metric}-${range.category}`}
                  y1={range.from}
                  y2={range.to === Infinity ? customYDomain[1] : range.to}
                  fill={range.color}
                  fillOpacity={0.15}
                  strokeOpacity={0}
                  ifOverflow="extendDomain"
                />
              ))}
              {viewMode === 'percentiles' && renderPercentileSeverityRanges()}
              
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                interval={0}
                minTickGap={50}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                domain={customYDomain}
                label={{ 
                  value: viewMode === 'scores' ? 'Score' : 'Percentile (%)', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
                allowDataOverflow={false}
                padding={{ top: 10, bottom: 0 }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '8px'
                }}
                formatter={(value: number, name: string) => {
                  if (viewMode === 'scores') {
                    const severity = getSeverityForScore(
                      value, 
                      metricTitle as 'Depression' | 'Anxiety' | 'Stress',
                      version
                    );
                    return [
                      <span key={`tooltip-${name}-${value}`} style={{ color }}>
                        {value} - <span style={{ color: severity.color }}>{severity.category}</span>
                      </span>,
                      metricTitle
                    ];
                  } else {
                    const severity = getSeverityForPercentile(value);
                    return [
                      <span key={`tooltip-${name}-${value}`} style={{ color }}>
                        {value}% - <span style={{ color: severity.color }}>{severity.category}</span>
                      </span>,
                      metricTitle
                    ];
                  }
                }}
              />
              <Line 
                type={curveType}
                dataKey={metricKey} 
                stroke={color} 
                name={metricTitle}
                strokeWidth={2}
                dot={{ r: 4 }}
                label={chartData.length === 1 ? {
                  position: 'top',
                  fill: color
                } : false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {severityInfo && (
          <div className="severity-indicator">
            <div className="current-value">
              Current: <span style={{ fontWeight: 'bold', color }}>{currentValue}</span>
            </div>
            <div className="severity-categories">
              {['Normal', 'Mild', 'Moderate', 'Severe', 'Extremely Severe'].map((category) => (
                <div 
                  key={category}
                  className={`severity-category ${severityInfo.category === category ? 'active' : ''}`}
                  style={{
                    backgroundColor: severityInfo.category === category 
                      ? SEVERITY_COLORS[category as DassSeverityCategory] 
                      : 'transparent',
                    borderColor: SEVERITY_COLORS[category as DassSeverityCategory]
                  }}
                >
                  {category}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {viewMode === 'percentiles' && !severityInfo && latestEntry && (
          <div className="percentile-indicator">
            <span style={{ fontWeight: 'bold', color }}>
              {Math.round(Number(currentValue))}% 
            </span> of patients score below this
          </div>
        )}

        {viewMode === 'scores' && (
          <div className="severity-range-labels">
            {severityRanges.map((range) => (
              <div 
                key={`${metric}-range-${range.category}`}
                className="severity-range-label"
                style={{ color: range.color }}
              >
                <span className="range-category">{range.category}:</span>
                <span className="range-values">
                  {range.from}-{range.to === Infinity ? '+' : range.to}
                </span>
              </div>
            ))}
          </div>
        )}
        
        {viewMode === 'percentiles' && (
          <div className="severity-range-labels">
            {PERCENTILE_SEVERITY_RANGES.map((range) => (
              <div 
                key={`percentile-range-${range.category}`}
                className="severity-range-label"
                style={{ color: range.color }}
              >
                <span className="range-category">{range.category}:</span>
                <span className="range-values">
                  {range.from}-{range.to}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDassChart = (data: TDass | null, title: string) => {
    if (!data) {
      return (
        <div className="dass-chart-container">
          <BaseText style={{ marginTop: '24px' }} type="title" className="chart-title">{title}</BaseText>
          <Empty description="No data available" />
        </div>
      );
    }
    if (!data.data.length) {
      return (
        <div className="dass-chart-container">
          <BaseText type="title" className="chart-title">{title}</BaseText>
          <div className="empty-state">
            <Empty description="No assessments completed yet" />
          </div>
        </div>
      );
    }

    // Sort data by date to ensure we get the most recent test
    const sortedData = [...data.data].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    
    // Get the latest test date
    const latestTestDate = sortedData.length ? sortedData[0].createdAt : '';

    const chartData = formatChartData(data.data);
    const statistics = calculateDassStatistics(data.data);
    const version = title.includes('DASS-21') ? 'DASS-21' : 'DASS-42';

    // Determine max scale based on view mode
    const maxPossibleScore = viewMode === 'scores' 
      ? (version === 'DASS-21' ? 21 : 42) 
      : 100; // for percentiles, max is always 100%
    
    const allScores = chartData.flatMap(entry => 
      viewMode === 'scores' 
        ? [Number(entry.depression), Number(entry.anxiety), Number(entry.stress)]
        : [Number(entry.depressionPercentile), Number(entry.anxietyPercentile), Number(entry.stressPercentile)]
    );
    
    const maxScore = viewMode === 'scores'
      ? Math.max(...allScores, Math.ceil(maxPossibleScore * 0.75))
      : 100; // for percentiles, max is always 100
    
    // Add some padding to the yDomain to prevent value cutoff
    const yDomain = viewMode === 'scores'
      ? [0, Math.ceil(maxScore * 1.1)]
      : [0, 105]; // For percentiles, add extra space beyond 100 to prevent cutoff
    
    const curveType: 'monotone' | undefined = chartData.length === 1 ? undefined : 'monotone';

    return (
      <div className="dass-chart-container">
        <BaseText type="title" className="chart-title">{title}</BaseText>
        
        <div className="control-panel">
          <div className="control-section">
            <BaseText type="subHeading" className="control-title">Data View</BaseText>
            <Radio.Group 
              value={viewMode} 
              onChange={(e: RadioChangeEvent) => setViewMode(e.target.value)}
              buttonStyle="solid"
              className="control-buttons"
            >
              <Radio.Button value="scores">Raw Scores</Radio.Button>
              <Radio.Button value="percentiles">Percentiles</Radio.Button>
            </Radio.Group>
            <BaseText type="caption" className="control-description">
              {viewMode === 'scores' 
                ? 'Showing actual assessment scores' 
                : 'Comparing to population percentiles'}
            </BaseText>
          </div>
          
          <div className="control-divider" />
          
          <div className="control-section">
            <BaseText type="subHeading" className="control-title">Chart Layout</BaseText>
            <Radio.Group 
              value={displayMode} 
              onChange={(e: RadioChangeEvent) => setDisplayMode(e.target.value)}
              buttonStyle="solid"
              className="control-buttons"
            >
              <Radio.Button value="combined">Combined</Radio.Button>
              <Radio.Button value="separate">Separate</Radio.Button>
            </Radio.Group>
            <BaseText type="caption" className="control-description">
              {displayMode === 'combined' 
                ? 'All metrics on one chart' 
                : 'Each metric on its own chart'}
            </BaseText>
          </div>
        </div>
        
        {latestTestDate && (
          <div style={{ textAlign: 'center', margin: '10px 0 15px' }}>
            <BaseText type="caption" style={{ opacity: 0.8 }}>
              Test date: {new Date(latestTestDate).toLocaleDateString()}
            </BaseText>
          </div>
        )}
        
        <div className="dass-statistics">
          <DassStatistics 
            title="Depression"
            metrics={statistics.depression}
            color={DASS_COLORS.depression}
            version={version}
          />
          <DassStatistics 
            title="Anxiety"
            metrics={statistics.anxiety}
            color={DASS_COLORS.anxiety}
            version={version}
          />
          <DassStatistics 
            title="Stress"
            metrics={statistics.stress}
            color={DASS_COLORS.stress}
            version={version}
          />
        </div>

        {displayMode === 'combined' ? (
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                {viewMode === 'scores' && visibleLines.depression && renderSeverityRanges(version, 'Depression')}
                {viewMode === 'scores' && visibleLines.anxiety && renderSeverityRanges(version, 'Anxiety')}
                {viewMode === 'scores' && visibleLines.stress && renderSeverityRanges(version, 'Stress')}
                {viewMode === 'percentiles' && renderPercentileSeverityRanges()}
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  interval={0}
                  minTickGap={50}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  domain={yDomain}
                  label={{ 
                    value: viewMode === 'scores' ? 'Score' : 'Percentile (%)', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { textAnchor: 'middle' }
                  }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px'
                  }}
                  formatter={(value: number, name: string) => {
                    const key = String(name).toLowerCase();
                    if (!visibleLines[key as keyof VisibleLines]) {
                      return ['-', name];
                    }

                    if (viewMode === 'scores') {
                      const severity = getSeverityForScore(
                        value, 
                        name as 'Depression' | 'Anxiety' | 'Stress',
                        version
                      );
                      return [
                        <span key={`tooltip-${name}-${value}`} style={{ color: DASS_COLORS[key as keyof typeof DASS_COLORS] }}>
                          {value} - <span style={{ color: severity.color }}>{severity.category}</span>
                        </span>,
                        name
                      ];
                    } else {
                      const severity = getSeverityForPercentile(value);
                      return [
                        <span key={`tooltip-${name}-${value}`} style={{ color: DASS_COLORS[key as keyof typeof DASS_COLORS] }}>
                          {value}% - <span style={{ color: severity.color }}>{severity.category}</span>
                        </span>,
                        name
                      ];
                    }
                  }}
                />
                {visibleLines.depression && (
                  <Line 
                    type={curveType}
                    dataKey={viewMode === 'scores' ? 'depression' : 'depressionPercentile'} 
                    stroke={DASS_COLORS.depression} 
                    name="Depression"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    label={chartData.length === 1 ? {
                      position: 'top',
                      fill: DASS_COLORS.depression
                    } : false}
                  />
                )}
                {visibleLines.anxiety && (
                  <Line 
                    type={curveType}
                    dataKey={viewMode === 'scores' ? 'anxiety' : 'anxietyPercentile'} 
                    stroke={DASS_COLORS.anxiety}
                    name="Anxiety"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    label={chartData.length === 1 ? {
                      position: 'top',
                      fill: DASS_COLORS.anxiety
                    } : false}
                  />
                )}
                {visibleLines.stress && (
                  <Line 
                    type={curveType}
                    dataKey={viewMode === 'scores' ? 'stress' : 'stressPercentile'} 
                    stroke={DASS_COLORS.stress}
                    name="Stress"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    label={chartData.length === 1 ? {
                      position: 'top',
                      fill: DASS_COLORS.stress
                    } : false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="separate-charts">
            {renderSingleMetricChart(chartData, 'depression', title, version, yDomain as [number, number])}
            {renderSingleMetricChart(chartData, 'anxiety', title, version, yDomain as [number, number])}
            {renderSingleMetricChart(chartData, 'stress', title, version, yDomain as [number, number])}
          </div>
        )}
        
        {displayMode === 'combined' && (
          <ChartLegend 
            visibleLines={visibleLines}
            onToggle={handleLegendClick}
          />
        )}
        
        {viewMode === 'scores' && renderSeverityLegend()}
        {viewMode === 'percentiles' && renderSeverityLegend()}
        <div className="dass-info">
          <button 
            className="severity-button"
            onClick={() => {
              setSelectedDass(data);
              setIsShowSeverity(true);
            }}
          >
            View Severity Ranges
          </button>
          {chartData.length === 1 && (
            <BaseText type="caption" className="single-point-note">
              Only one assessment completed. Complete more assessments to track changes over time.
            </BaseText>
          )}
        </div>
      </div>
    );
  };

  const exportDassToPDF = async (version: 'DASS-21' | 'DASS-42', data: TDass) => {
    if (!data || !data.data.length) {
      showErrorToast('No data available to export');
      return;
    }

    message.loading({ content: `Generating ${version} PDF report...`, key: 'pdfExport' });
    
    try {
      // Initialize PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 40;
      let yOffset = margin + 60; // Extra space for logo
      
      // Add ANTSA logo at the top - using imported logo
      try {
        // Import and convert logo to data URL
        const img = document.createElement('img');
        img.src = logoImage;
        
        // Wait for image to load, then add to PDF
        await new Promise<void>((resolve) => {
          img.onload = () => {
            // Position logo centered at the top
            const logoWidth = 110;
            const logoHeight = 44;
            const logoX = (pageWidth - logoWidth) / 2;
            
            // Create canvas to convert image to data URL
            const canvas = document.createElement('canvas');
            canvas.width = logoWidth;
            canvas.height = logoHeight;
            const ctx = canvas.getContext('2d');
            
            if (ctx) {
              ctx.drawImage(img, 0, 0, logoWidth, logoHeight);
              const dataUrl = canvas.toDataURL('image/png');
              
              // Add image to PDF
              pdf.addImage(dataUrl, 'PNG', logoX, margin, logoWidth, logoHeight);
            }
            resolve();
          };
          
          img.onerror = () => {
            console.error('Error loading logo image');
            resolve(); // Continue without logo
          };
        });
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
        // Continue without logo if there's an error
      }
      
      // Add title and basic information with improved spacing
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${version} Assessment Report`, margin, yOffset);
      yOffset += 40; // More breathing room after title
      
      // Add divider after title
      pdf.setDrawColor(180, 180, 180);
      pdf.line(margin, yOffset - 20, pageWidth - margin, yOffset - 20);
      
      // Add client info with better spacing
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Client ID: ${clientId}`, margin, yOffset);
      yOffset += 18;
      pdf.text(`Report Generated: ${dayjs().format('MMMM D, YYYY h:mm A')}`, margin, yOffset);
      yOffset += 18;
      
      // Sort data by date and get most recent assessment
      const sortedData = [...data.data].sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      
      const latestAssessment = sortedData[0];
      if (latestAssessment) {
        pdf.text(`Latest Assessment Date: ${dayjs(latestAssessment.createdAt).format('MMMM D, YYYY')}`, margin, yOffset);
        yOffset += 30;
      }
      
      // Format scores for display
      const formatChartData = (data: Array<{ 
        createdAt: string; 
        summary: THomeworkSummaryScore;
        percentiles?: {
          Depression: number | null;
          Anxiety: number | null;
          Stress: number | null;
        };
      }>) => {
        return data.map(entry => ({
          date: new Date(entry.createdAt).toLocaleDateString(),
          depression: entry.summary?.Depression?.score || 0,
          anxiety: entry.summary?.Anxiety?.score || 0,
          stress: entry.summary?.Stress?.score || 0,
          depressionPercentile: entry.percentiles?.Depression || 0,
          anxietyPercentile: entry.percentiles?.Anxiety || 0,
          stressPercentile: entry.percentiles?.Stress || 0
        }));
      };
      
      const chartData = formatChartData(data.data);
      const statistics = calculateDassStatistics(data.data);
      
      // Add summary section with better spacing and formatting
      pdf.setFillColor(245, 245, 245);
      pdf.rect(margin, yOffset, pageWidth - margin * 2, 26, 'F');
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(50, 50, 50);
      pdf.text('Assessment Summary', margin + 10, yOffset + 18);
      yOffset += 50; // Increased from 40 to 50 to fix clipping issue
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      // Add summary table with better visual separation
      const columnWidth = 110;
      const rowHeight = 25; // Restored height for better readability
      const tableX = margin;
      let tableY = yOffset;
      
      // Table header with background
      pdf.setFillColor(230, 230, 230);
      pdf.rect(tableX, tableY - rowHeight + 5, columnWidth * 4, rowHeight, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Scale', tableX + 10, tableY);
      pdf.text('Latest Score', tableX + columnWidth, tableY);
      pdf.text('Percentile', tableX + columnWidth * 2, tableY);
      pdf.text('Severity', tableX + columnWidth * 3, tableY);
      tableY += rowHeight;
      
      // Add slight shadows to cells for depth
      const addTableRow = (label: string, score: number, percentile: number, severity: {category: string, color: string}) => {
        // Light background for odd rows
        if ((tableY - yOffset) / rowHeight % 2 === 1) {
          pdf.setFillColor(248, 248, 248);
          pdf.rect(tableX, tableY - rowHeight + 5, columnWidth * 4, rowHeight, 'F');
        }
        
        pdf.setFont('helvetica', 'normal');
        pdf.text(label, tableX + 10, tableY);
        pdf.text(score.toString(), tableX + columnWidth, tableY);
        pdf.text(`${Math.round(percentile)}%`, tableX + columnWidth * 2, tableY);
        
        // Set severity text color
        pdf.setTextColor(severity.color);
        pdf.text(severity.category, tableX + columnWidth * 3, tableY);
        pdf.setTextColor(0, 0, 0); // Reset to black
        
        tableY += rowHeight;
      };
      
      // Add Depression row
      const depressionLatest = statistics.depression.current;
      const depressionSeverity = getSeverityForScore(
        depressionLatest, 
        'Depression', 
        version
      );
      const depressionPercentile = latestAssessment.percentiles?.Depression || 0;
      
      addTableRow('Depression', depressionLatest, depressionPercentile, depressionSeverity);
      
      // Add Anxiety row
      const anxietyLatest = statistics.anxiety.current;
      const anxietySeverity = getSeverityForScore(
        anxietyLatest, 
        'Anxiety', 
        version
      );
      const anxietyPercentile = latestAssessment.percentiles?.Anxiety || 0;
      
      addTableRow('Anxiety', anxietyLatest, anxietyPercentile, anxietySeverity);
      
      // Add Stress row
      const stressLatest = statistics.stress.current;
      const stressSeverity = getSeverityForScore(
        stressLatest, 
        'Stress', 
        version
      );
      const stressPercentile = latestAssessment.percentiles?.Stress || 0;
      
      addTableRow('Stress', stressLatest, stressPercentile, stressSeverity);
      
      yOffset = tableY + 15; // Space after the table
      
      // Assessment Overview section with better formatting
      pdf.setFillColor(245, 245, 245);
      pdf.rect(margin, yOffset, pageWidth - margin * 2, 26, 'F');
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(50, 50, 50);
      pdf.text('Assessment Overview', margin + 10, yOffset + 18);
      yOffset += 35; // Reduced from 40 to 35 to help fit on one page
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      // Condensed interpretation text with bullet points for better readability
      const formatInterpretationText = (metric: string, score: number, severity: {category: string}) => {
        let interpretationText = '';
        if (severity.category === 'Normal') {
          interpretationText = 'Normal levels';
        } else if (severity.category === 'Mild') {
          interpretationText = 'Mild symptoms, may benefit from monitoring';
        } else if (severity.category === 'Moderate') {
          interpretationText = 'Moderate symptoms, may benefit from intervention';
        } else if (severity.category === 'Severe') {
          interpretationText = 'Severe symptoms, requires intervention';
        } else {
          interpretationText = 'Extremely severe symptoms, requires immediate intervention';
        }
        return `• ${metric} (${severity.category}): Score ${score} – ${interpretationText}`;
      };
      
      const depressionText = formatInterpretationText('Depression', depressionLatest, depressionSeverity);
      pdf.text(depressionText, margin, yOffset);
      yOffset += 18; // Reduced from 20 to 18
      
      const anxietyText = formatInterpretationText('Anxiety', anxietyLatest, anxietySeverity);
      pdf.text(anxietyText, margin, yOffset);
      yOffset += 18; // Reduced from 20 to 18
      
      const stressText = formatInterpretationText('Stress', stressLatest, stressSeverity);
      pdf.text(stressText, margin, yOffset);
      yOffset += 25; // Reduced from 30 to 25
      
      // Skip trend analysis section if space is getting tight, but with better spacing
      if (yOffset < pageHeight - 220 && chartData.length > 1) { // Reduced threshold from 240 to 220
        pdf.setFillColor(245, 245, 245);
        pdf.rect(margin, yOffset, pageWidth - margin * 2, 26, 'F');
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(50, 50, 50);
        pdf.text('Trend Analysis', margin + 10, yOffset + 18);
        yOffset += 35; // Reduced from 40 to 35
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        const getTrendIcon = (trend: string) => {
          return trend === 'improving' ? '↓ improving' : 
                trend === 'worsening' ? '↑ worsening' : 
                '→ stable';
        };
        
        pdf.text(`• The client has completed ${chartData.length} assessments.`, margin, yOffset);
        yOffset += 18; // Reduced from 20 to 18
        pdf.text(`• Depression: ${getTrendIcon(statistics.depression.trend)}`, margin, yOffset);
        yOffset += 18; // Reduced from 20 to 18
        pdf.text(`• Anxiety: ${getTrendIcon(statistics.anxiety.trend)}`, margin, yOffset);
        yOffset += 18; // Reduced from 20 to 18
        pdf.text(`• Stress: ${getTrendIcon(statistics.stress.trend)}`, margin, yOffset);
        yOffset += 25; // Reduced from 30 to 25
      }
      
      // Brief about DASS with better formatting
      if (yOffset < pageHeight - 180) { // Reduced threshold from 200 to 180
        pdf.setFillColor(245, 245, 245);
        pdf.rect(margin, yOffset, pageWidth - margin * 2, 26, 'F');
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(50, 50, 50);
        pdf.text('About the DASS Assessment', margin + 10, yOffset + 18);
        yOffset += 35; // Reduced from 40 to 35
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        const aboutText = `The Depression Anxiety Stress Scales (${version}) is a self-report instrument designed to measure the negative emotional states of depression, anxiety, and stress. The scale uses a 4-point severity/frequency scale to rate the extent to which clients have experienced each state over the past week.`;
        
        const aboutLines = pdf.splitTextToSize(aboutText, pageWidth - (margin * 2));
        pdf.text(aboutLines, margin, yOffset);
        yOffset += aboutLines.length * 15 + 5;
        
        const scoreText = version === 'DASS-21' 
          ? 'The DASS-21 is a shortened version of the DASS-42, with 7 items per scale. Scores from the DASS-21 are multiplied by 2 to make them comparable to the DASS-42 scores.'
          : 'The DASS-42 consists of 42 items, with 14 items per scale (Depression, Anxiety, Stress).';
        
        const scoreLines = pdf.splitTextToSize(scoreText, pageWidth - (margin * 2));
        pdf.text(scoreLines, margin, yOffset);
        yOffset += scoreLines.length * 15 + 10; // Reduced from 15 to 10
      }
      
      // Check if we need a new page for the severity table
      if (yOffset > pageHeight - 220) { // Increased threshold from 180 to 220 to ensure enough space for heading and table
        pdf.addPage();
        yOffset = margin + 60;
      }
      
      // Add severity ranges table with better styling
      pdf.setFillColor(245, 245, 245);
      pdf.rect(margin, yOffset, pageWidth - margin * 2, 26, 'F');
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(50, 50, 50);
      pdf.text('Severity Rating Ranges', margin + 10, yOffset + 18);
      yOffset += 50; // Increased from 35 to 50 to fix clipping issue, matching Assessment Summary
      
      // Draw severity table
      const severityTableX = margin;
      let severityTableY = yOffset;
      const severityColumnWidth = 100;
      const severityRowHeight = 22;
      
      // Get appropriate ranges for this version
      const depRangesBase = getDass21SeverityRanges('Depression');
      const anxRangesBase = getDass21SeverityRanges('Anxiety');
      const stressRangesBase = getDass21SeverityRanges('Stress');
      
      const depRanges = version === 'DASS-42' ? getDass42SeverityRanges('Depression') : depRangesBase;
      const anxRanges = version === 'DASS-42' ? getDass42SeverityRanges('Anxiety') : anxRangesBase;
      const stressRanges = version === 'DASS-42' ? getDass42SeverityRanges('Stress') : stressRangesBase;
      
      // Table header with background
      pdf.setFillColor(230, 230, 230);
      pdf.rect(severityTableX, severityTableY - severityRowHeight + 5, severityColumnWidth * 4, severityRowHeight, 'F');
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Severity', severityTableX + 10, severityTableY);
      pdf.text('Depression', severityTableX + severityColumnWidth, severityTableY);
      pdf.text('Anxiety', severityTableX + severityColumnWidth * 2, severityTableY);
      pdf.text('Stress', severityTableX + severityColumnWidth * 3, severityTableY);
      severityTableY += severityRowHeight;
      
      // Add each severity category row
      const categories: DassSeverityCategory[] = ['Normal', 'Mild', 'Moderate', 'Severe', 'Extremely Severe'];
      
      categories.forEach((category, index) => {
        const depRange = depRanges.find(r => r.category === category);
        const anxRange = anxRanges.find(r => r.category === category);
        const stressRange = stressRanges.find(r => r.category === category);
        
        // Light background for odd rows
        if (index % 2 === 1) {
          pdf.setFillColor(248, 248, 248);
          pdf.rect(severityTableX, severityTableY - severityRowHeight + 5, severityColumnWidth * 4, severityRowHeight, 'F');
        }
        
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(SEVERITY_COLORS[category]);
        pdf.text(category, severityTableX + 10, severityTableY);
        pdf.setTextColor(0, 0, 0); // Reset to black
        
        if (depRange) {
          const rangeText = depRange.to === Infinity 
            ? `${depRange.from}+` 
            : `${depRange.from}-${depRange.to}`;
          pdf.text(rangeText, severityTableX + severityColumnWidth, severityTableY);
        }
        
        if (anxRange) {
          const rangeText = anxRange.to === Infinity 
            ? `${anxRange.from}+` 
            : `${anxRange.from}-${anxRange.to}`;
          pdf.text(rangeText, severityTableX + severityColumnWidth * 2, severityTableY);
        }
        
        if (stressRange) {
          const rangeText = stressRange.to === Infinity 
            ? `${stressRange.from}+` 
            : `${stressRange.from}-${stressRange.to}`;
          pdf.text(rangeText, severityTableX + severityColumnWidth * 3, severityTableY);
        }
        
        severityTableY += severityRowHeight;
      });
      
      // Add logo in footer as well - using imported logo
      try {
        // Create footer logo from same image source
        const img = document.createElement('img');
        img.src = logoImage;
        
        // Wait for image to load, then add to PDF
        await new Promise<void>((resolve) => {
          img.onload = () => {
            // Position logo at bottom left
            const footerLogoWidth = 55; // Half size for footer
            const footerLogoHeight = 22;
            const footerY = pageHeight - margin;
            
            // Create canvas to convert image to data URL
            const canvas = document.createElement('canvas');
            canvas.width = footerLogoWidth;
            canvas.height = footerLogoHeight;
            const ctx = canvas.getContext('2d');
            
            if (ctx) {
              ctx.drawImage(img, 0, 0, footerLogoWidth, footerLogoHeight);
              const dataUrl = canvas.toDataURL('image/png');
              
              // Add image to PDF
              pdf.addImage(dataUrl, 'PNG', margin, footerY - footerLogoHeight, footerLogoWidth, footerLogoHeight);
            }
            
            // Add footer with proper spacing from the table
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(100, 100, 100);
            pdf.text('This report was automatically generated by ANTSA. It should be used in conjunction with clinical judgment.', 
                   margin + footerLogoWidth + 10, footerY - 8);
            
            resolve();
          };
          
          img.onerror = () => {
            console.error('Error loading footer logo image');
            // Add footer without logo if image fails to load
            const footerY = pageHeight - margin;
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(100, 100, 100);
            pdf.text('This report was automatically generated by ANTSA. It should be used in conjunction with clinical judgment.', 
                   margin, footerY);
            resolve();
          };
        });
      } catch (error) {
        // If logo fails, just add the footer text
        const footerY = pageHeight - margin;
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(100, 100, 100);
        pdf.text('This report was automatically generated by ANTSA. It should be used in conjunction with clinical judgment.', 
                margin, footerY);
      }
      
      // Generate filename and save
      const filename = `${version.toLowerCase().replace('-', '')}_report_${dayjs().format('YYYY-MM-DD')}.pdf`;
      pdf.save(filename);
      
      message.success({ content: 'PDF report generated successfully', key: 'pdfExport' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error({ content: 'Failed to generate PDF report', key: 'pdfExport' });
    }
  };

  const renderExportButton = (data: TDass | null, version: 'DASS-21' | 'DASS-42') => {
    if (!data || !data.data.length) {
      return null;
    }
    
    return (
      <Button
        type="primary"
        icon={<FilePdfOutlined />}
        onClick={() => exportDassToPDF(version, data)}
        className="export-button"
      >
        Export {version} Report
      </Button>
    );
  };

  if (loading) {
    return (
      <div className="dass-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="dass-container">
      <div className="dass-export-controls">
        {renderExportButton(dassData.dass21, 'DASS-21')}
        {renderExportButton(dassData.dass42, 'DASS-42')}
      </div>
      {renderDassChart(dassData.dass21, 'DASS-21')}
      {renderDassChart(dassData.dass42, 'DASS-42')}
      
      <ModalSeverity 
        open={isShowSeverity} 
        data={selectedDass?.severity} 
        onCancel={() => setIsShowSeverity(false)} 
      />
    </div>
  );
};

export default Dass;
